import { useRef, useState } from "react"
import { Badge, Button, Form, Table } from "react-bootstrap"
import { buildVrboIntegrationPayload } from "../../../Components/VRBOIntegration/useVrboIntegrationRecord"
import {
    useAddNewIntegrationMutation,
    useLazyGetIntegrationsQuery,
    useVrboQueryPropertyMutation,
    useVrboUpdatePropertyAvailabilityMutation,
    useVrboUpdatePropertyPricingMutation,
} from "../../../services/bClientApi"
import { errorMessage, STATUS_VARIANT, StepStatus } from "./vrboMapperShared"

const STEP_KEYS = ['lookup', 'query', 'create', 'pricing', 'availability'] as const
type StepKey = typeof STEP_KEYS[number]

const STEP_LABELS: Record<StepKey, string> = {
    lookup: 'Find Integration',
    query: 'Query VRBO',
    create: 'Create Integration',
    pricing: 'Pricing',
    availability: 'Availability',
}

type RowState = {
    propertyId: string
    steps: Record<StepKey, StepStatus>
    message?: string
}

function parsePropertyIds(text: string) {
    const ids = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
    return Array.from(new Set(ids))
}

function newRow(propertyId: string): RowState {
    return {
        propertyId,
        steps: { lookup: 'pending', query: 'pending', create: 'pending', pricing: 'pending', availability: 'pending' },
    }
}

export default function VrboMapMode() {
    const [text, setText] = useState('')
    const [rows, setRows] = useState<RowState[]>([])
    const [isRunning, setIsRunning] = useState(false)
    const cancelRef = useRef(false)

    const [findIntegrations] = useLazyGetIntegrationsQuery()
    const [queryVRBOProperty] = useVrboQueryPropertyMutation()
    const [createIntegration] = useAddNewIntegrationMutation()
    const [updatePricing] = useVrboUpdatePropertyPricingMutation()
    const [updateAvailability] = useVrboUpdatePropertyAvailabilityMutation()

    const updateRow = (index: number, changes: Partial<Omit<RowState, 'steps'>> & { steps?: Partial<Record<StepKey, StepStatus>> }) => {
        setRows((current) => current.map((row, idx) => idx !== index ? row : {
            ...row,
            ...changes,
            steps: { ...row.steps, ...changes.steps },
        }))
    }

    // runs one step, recording its status; rethrows so the caller stops processing this property
    const runStep = async <T,>(index: number, step: StepKey, action: () => Promise<T>) => {
        updateRow(index, { steps: { [step]: 'running' } })
        try {
            const result = await action()
            updateRow(index, { steps: { [step]: 'success' } })
            return result
        } catch (error) {
            console.error(`VRBO mapper: ${step} failed`, error)
            updateRow(index, { steps: { [step]: 'error' }, message: `${STEP_LABELS[step]}: ${errorMessage(error)}` })
            throw error
        }
    }

    const processProperty = async (index: number, propertyId: string) => {
        try {
            const existing = await runStep(index, 'lookup', async () => {
                const response = await findIntegrations({
                    integrationsQuery: { entityType: 'property', entityId: propertyId, provider: 'vrbo' },
                    pagination: { page: 1, limit: 1 },
                }, false).unwrap()
                return response?.docs?.[0] || null
            })

            if (existing) {
                updateRow(index, { steps: { query: 'skipped', create: 'skipped' } })
            } else {
                const vrboPropertyData = await runStep(index, 'query', async () => {
                    const response = await queryVRBOProperty(propertyId).unwrap()
                    if (!response?.response?.property) {
                        throw new Error('No property data returned from VRBO')
                    }
                    return response
                })
                await runStep(index, 'create', () =>
                    createIntegration(buildVrboIntegrationPayload(propertyId, vrboPropertyData)).unwrap()
                )
            }

            await runStep(index, 'pricing', () => updatePricing(propertyId).unwrap())
            await runStep(index, 'availability', () => updateAvailability(propertyId).unwrap())
        } catch {
            // the failing step has already been recorded on the row
        }
    }

    const handleRun = async () => {
        const propertyIds = parsePropertyIds(text)
        if (!propertyIds.length) return

        cancelRef.current = false
        setRows(propertyIds.map(newRow))
        setIsRunning(true)

        for (let index = 0; index < propertyIds.length; index++) {
            if (cancelRef.current) break
            await processProperty(index, propertyIds[index])
        }

        setIsRunning(false)
    }

    const propertyCount = parsePropertyIds(text).length
    const completedCount = rows.filter((row) => STEP_KEYS.every((key) => row.steps[key] === 'success' || row.steps[key] === 'skipped')).length
    const failedCount = rows.filter((row) => STEP_KEYS.some((key) => row.steps[key] === 'error')).length

    return (
        <>
            <Form.Group className="mb-3">
                <Form.Label>Property IDs (one per line)</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={10}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    disabled={isRunning}
                    placeholder={'propertyId1\npropertyId2'}
                />
            </Form.Group>

            <div className="d-flex align-items-center gap-2 mb-3">
                <Button onClick={handleRun} disabled={isRunning || !propertyCount}>
                    {isRunning ? 'Running...' : `Run (${propertyCount} ${propertyCount === 1 ? 'property' : 'properties'})`}
                </Button>
                {isRunning && (
                    <Button variant="outline-danger" onClick={() => { cancelRef.current = true }}>
                        Stop after current property
                    </Button>
                )}
                {rows.length > 0 && (
                    <small className="text-muted">
                        {completedCount} of {rows.length} complete{failedCount > 0 && `, ${failedCount} failed`}
                    </small>
                )}
            </div>

            {rows.length > 0 && (
                <Table size="sm" bordered responsive>
                    <thead>
                        <tr>
                            <th>Property ID</th>
                            {STEP_KEYS.map((key) => (
                                <th key={key}>{STEP_LABELS[key]}</th>
                            ))}
                            <th>Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.propertyId}>
                                <td><code>{row.propertyId}</code></td>
                                {STEP_KEYS.map((key) => (
                                    <td key={key}>
                                        <Badge bg={STATUS_VARIANT[row.steps[key]]} text={row.steps[key] === 'pending' ? 'dark' : undefined}>
                                            {row.steps[key]}
                                        </Badge>
                                    </td>
                                ))}
                                <td className="text-danger">{row.message}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </>
    )
}
